"use client"

import React, { useState, useEffect, useCallback, useMemo, useRef } from "react"
import { Plus, Edit, Trash2, Play, Pause, Settings, CheckCircle, XCircle, X, ChevronDown } from "lucide-react"
import { Pagination } from "./pagination"
import apiService from '../../services/apiService'

// ─── Types ────────────────────────────────────────────────────────────────────

interface CustomAlertRule {
  id: string
  name: string
  description: string
  is_active: boolean
  condition_type: 'threshold' | 'string_match' | 'status_check' | 'combined'
  field_path: string
  operator: string
  threshold_value?: number
  string_value?: string
  alert_type: string
  alert_title: string
  alert_description_template: string
  severity: 'critical' | 'high' | 'medium' | 'low'
  device_type?: 'onpremise' | 'gateway' | 'all'
  activation_status_filter?: 'all' | 'activated' | 'trial'
  site_id?: string
  client_id?: string
  cooldown_minutes: number
  created_at: string
  updated_at: string
}

interface CustomAlertRulesProps {
  onRuleChange?: () => void
}

// ─── Styles ───────────────────────────────────────────────────────────────────

const S = {
  card: {
    background: 'linear-gradient(135deg, #1a1a1a 0%, #2a2a2a 100%)',
    border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: '12px',
    padding: '20px',
  } as React.CSSProperties,
  formCard: {
    background: 'linear-gradient(135deg, #1a1a1a 0%, #2a2a2a 100%)',
    border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: '12px',
    padding: '24px',
    marginBottom: '16px',
  } as React.CSSProperties,
  label: {
    display: 'block',
    fontSize: '12px',
    fontWeight: 500,
    color: '#94a3b8',
    marginBottom: '6px',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.05em',
  } as React.CSSProperties,
  input: {
    width: '100%',
    backgroundColor: '#252525',
    border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: '8px',
    padding: '8px 12px',
    color: '#e2e8f0',
    fontSize: '14px',
    outline: 'none',
    boxSizing: 'border-box' as const,
    appearance: 'none' as const,
    WebkitAppearance: 'none' as const,
    MozAppearance: 'none' as const,
  } as React.CSSProperties,
  textarea: {
    width: '100%',
    backgroundColor: '#252525',
    border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: '8px',
    padding: '8px 12px',
    color: '#e2e8f0',
    fontSize: '14px',
    outline: 'none',
    resize: 'vertical' as const,
    minHeight: '80px',
    boxSizing: 'border-box' as const,
    appearance: 'none' as const,
    WebkitAppearance: 'none' as const,
    MozAppearance: 'none' as const,
  } as React.CSSProperties,
  btnPrimary: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    backgroundColor: '#00A9A7',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    padding: '8px 16px',
    fontSize: '14px',
    fontWeight: 500,
    cursor: 'pointer',
  } as React.CSSProperties,
  btnOutline: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    backgroundColor: 'transparent',
    color: '#94a3b8',
    border: '1px solid rgba(255,255,255,0.15)',
    borderRadius: '8px',
    padding: '8px 16px',
    fontSize: '14px',
    cursor: 'pointer',
  } as React.CSSProperties,
  btnIcon: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'transparent',
    color: '#94a3b8',
    border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: '6px',
    padding: '6px',
    cursor: 'pointer',
  } as React.CSSProperties,
  sectionTitle: {
    fontSize: '14px',
    fontWeight: 600,
    color: '#94a3b8',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.08em',
    marginBottom: '12px',
    paddingBottom: '8px',
    borderBottom: '1px solid rgba(255,255,255,0.06)',
  } as React.CSSProperties,
  fieldError: {
    display: 'block',
    fontSize: '11px',
    color: '#ef4444',
    marginTop: '5px',
  } as React.CSSProperties,
}

// ─── Custom Select ────────────────────────────────────────────────────────────

interface SelectOption { value: string; label: string }

function CustomSelect({
  value,
  onChange,
  options,
  compact = false,
}: {
  value: string
  onChange: (value: string) => void
  options: SelectOption[]
  compact?: boolean
}) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [open])

  const selected = options.find(o => o.value === value)
  const padding = compact ? '4px 32px 4px 10px' : '8px 36px 8px 12px'
  const fontSize = compact ? '12px' : '14px'

  return (
    <div ref={ref} style={{ position: 'relative', width: compact ? 'auto' : '100%' }}>
      {/* Trigger */}
      <button
        type="button"
        onClick={() => setOpen(prev => !prev)}
        style={{
          width: compact ? 'auto' : '100%',
          backgroundColor: '#252525',
          border: `1px solid ${open ? 'rgba(0,169,167,0.5)' : 'rgba(255,255,255,0.1)'}`,
          borderRadius: open ? '8px 8px 0 0' : '8px',
          padding,
          color: '#e2e8f0',
          fontSize,
          outline: 'none',
          cursor: 'pointer',
          textAlign: 'left',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '8px',
          whiteSpace: 'nowrap',
          transition: 'border-color 0.15s',
          boxSizing: 'border-box',
          appearance: 'none',
          WebkitAppearance: 'none',
          MozAppearance: 'none',
        }}
      >
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {selected?.label ?? ''}
        </span>
        <ChevronDown
          size={compact ? 12 : 14}
          color="#94a3b8"
          style={{ flexShrink: 0, transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }}
        />
      </button>

      {/* Dropdown panel */}
      {open && (
        <div style={{
          position: 'absolute',
          top: '100%',
          left: 0,
          right: compact ? undefined : 0,
          minWidth: compact ? '120px' : undefined,
          zIndex: 600,
          backgroundColor: '#252525',
          border: '1px solid rgba(0,169,167,0.3)',
          borderTop: 'none',
          borderRadius: '0 0 8px 8px',
          maxHeight: '220px',
          overflowY: 'auto',
          boxShadow: '0 12px 40px rgba(0,0,0,0.6)',
        }}>
          {options.map(opt => {
            const isSelected = opt.value === value
            return (
              <div
                key={opt.value}
                onMouseDown={e => { e.preventDefault(); onChange(opt.value); setOpen(false) }}
                style={{
                  padding: compact ? '6px 12px' : '8px 14px',
                  color: isSelected ? '#00A9A7' : '#cbd5e1',
                  backgroundColor: isSelected ? 'rgba(0,169,167,0.12)' : 'transparent',
                  cursor: 'pointer',
                  fontSize,
                  transition: 'background 0.1s',
                  borderLeft: isSelected ? '2px solid #00A9A7' : '2px solid transparent',
                }}
                onMouseEnter={e => {
                  if (!isSelected) (e.currentTarget as HTMLDivElement).style.backgroundColor = 'rgba(255,255,255,0.06)'
                }}
                onMouseLeave={e => {
                  if (!isSelected) (e.currentTarget as HTMLDivElement).style.backgroundColor = 'transparent'
                }}
              >
                {opt.label}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

// ─── Select option constants ──────────────────────────────────────────────────

const CONDITION_TYPE_OPTIONS: SelectOption[] = [
  { value: 'threshold',    label: 'Threshold Check' },
  { value: 'string_match', label: 'String Match' },
  { value: 'status_check', label: 'Status Check' },
  { value: 'combined',     label: 'Combined' },
]

const OPERATOR_OPTIONS: SelectOption[] = [
  { value: 'gt',  label: 'Greater than (>)' },
  { value: 'gte', label: 'Greater than or equal (>=)' },
  { value: 'lt',  label: 'Less than (<)' },
  { value: 'lte', label: 'Less than or equal (<=)' },
  { value: 'eq',  label: 'Equal (=)' },
  { value: 'ne',  label: 'Not equal (≠)' },
]

const SEVERITY_OPTIONS: SelectOption[] = [
  { value: 'critical', label: 'Critical' },
  { value: 'high',     label: 'High' },
  { value: 'medium',   label: 'Medium' },
  { value: 'low',      label: 'Low' },
]

const DEVICE_TYPE_OPTIONS: SelectOption[] = [
  { value: 'onpremise', label: 'AI Systems (On-Premise)' },
  { value: 'gateway',   label: 'Gateways' },
  { value: 'all',       label: 'All devices' },
]

const ACTIVATION_STATUS_OPTIONS: SelectOption[] = [
  { value: 'all',       label: 'All machines' },
  { value: 'activated', label: 'Activated only' },
  { value: 'trial',     label: 'Trial only' },
]

const PAGE_SIZE_OPTIONS: SelectOption[] = [
  { value: '10', label: '10 / page' },
  { value: '25', label: '25 / page' },
  { value: '50', label: '50 / page' },
]

// ─── Confirm dialog ───────────────────────────────────────────────────────────

interface ConfirmDialogProps {
  message: string
  onConfirm: () => void
  onCancel: () => void
  confirmLabel?: string
  danger?: boolean
}

function ConfirmDialog({ message, onConfirm, onCancel, confirmLabel = 'Confirm', danger = false }: ConfirmDialogProps) {
  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 10000,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      backgroundColor: 'rgba(0,0,0,0.65)',
      backdropFilter: 'blur(4px)',
    }}
      onClick={onCancel}
    >
      <div
        style={{
          background: '#0d1117',
          border: '1px solid rgba(255,255,255,0.1)',
          borderRadius: '14px',
          padding: '28px 28px 24px',
          minWidth: '340px',
          maxWidth: '440px',
          boxShadow: '0 24px 64px rgba(0,0,0,0.6)',
        }}
        onClick={e => e.stopPropagation()}
      >
        <p style={{ color: '#e2e8f0', fontSize: '15px', margin: '0 0 24px', lineHeight: '1.5' }}>
          {message}
        </p>
        <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
          <button
            onClick={onCancel}
            style={{
              background: 'rgba(255,255,255,0.05)',
              border: '1px solid rgba(255,255,255,0.12)',
              borderRadius: '8px',
              color: '#94a3b8',
              cursor: 'pointer',
              fontSize: '14px',
              padding: '8px 18px',
            }}
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            style={{
              background: danger ? 'rgba(239,68,68,0.15)' : 'rgba(0,169,167,0.15)',
              border: `1px solid ${danger ? 'rgba(239,68,68,0.4)' : 'rgba(0,169,167,0.4)'}`,
              borderRadius: '8px',
              color: danger ? '#ef4444' : '#00A9A7',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: 600,
              padding: '8px 18px',
            }}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  )
}

// ─── Toast ────────────────────────────────────────────────────────────────────

interface ToastProps { type: 'success' | 'error'; message: string; onClose: () => void }

function Toast({ type, message, onClose }: ToastProps) {
  useEffect(() => {
    const t = setTimeout(onClose, 4000)
    return () => clearTimeout(t)
  }, [onClose])

  const isSuccess = type === 'success'
  return (
    <div style={{
      position: 'fixed', bottom: '24px', right: '24px', zIndex: 9999,
      display: 'flex', alignItems: 'center', gap: '10px',
      background: isSuccess ? 'rgba(0,169,167,0.15)' : 'rgba(239,68,68,0.15)',
      border: `1px solid ${isSuccess ? 'rgba(0,169,167,0.4)' : 'rgba(239,68,68,0.4)'}`,
      borderRadius: '10px', padding: '12px 16px',
      fontSize: '14px', fontWeight: 500,
      boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
      minWidth: '280px',
    }}>
      {isSuccess ? <CheckCircle size={16} color="#00A9A7" /> : <XCircle size={16} color="#ef4444" />}
      <span style={{ flex: 1, color: '#e2e8f0' }}>{message}</span>
      <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', padding: 0 }}>
        <X size={14} />
      </button>
    </div>
  )
}

// ─── Severity badge ───────────────────────────────────────────────────────────

const SEVERITY_STYLES: Record<string, { bg: string; color: string; border: string }> = {
  critical: { bg: 'rgba(239,68,68,0.15)',  color: '#ef4444', border: 'rgba(239,68,68,0.4)' },
  high:     { bg: 'rgba(255,134,151,0.15)', color: '#ff8697', border: 'rgba(255,134,151,0.4)' },
  medium:   { bg: 'rgba(234,179,8,0.15)',  color: '#eab308', border: 'rgba(234,179,8,0.4)' },
  low:      { bg: 'rgba(34,197,94,0.15)',  color: '#22c55e', border: 'rgba(34,197,94,0.4)' },
}

function SeverityBadge({ severity }: { severity: string }) {
  const s = SEVERITY_STYLES[severity] || SEVERITY_STYLES.medium
  return (
    <span style={{
      background: s.bg, color: s.color,
      border: `1px solid ${s.border}`,
      borderRadius: '6px', padding: '2px 8px',
      fontSize: '11px', fontWeight: 600,
      textTransform: 'uppercase', letterSpacing: '0.05em',
    }}>
      {severity}
    </span>
  )
}

// ─── Alert type options ───────────────────────────────────────────────────────

const ALERT_TYPES: [string, string][] = [
  ['service_unreachable',       'Service Unreachable'],
  ['qure_services_unreachable', 'Qure Services Unreachable'],
  ['high_unprocessed_images',   'High Unprocessed Images'],
  ['pipeline_failure',          'Pipeline Failure'],
  ['database_connection',       'Database Connection'],
  ['api_gateway_failure',       'API Gateway Failure'],
  ['gateway_offline',           'Gateway Offline'],
  ['heartbeat_missing',         'Heartbeat Missing'],
  ['pacs_connection_lost',      'PACS Connection Lost'],
  ['forwarding_failure',        'Forwarding Failure'],
  ['failed_image_upload',       'Failed Image Upload'],
  ['failed_images_detected',    'Failed Images Detected'],
  ['cpu_usage_high',            'CPU Usage High'],
  ['processing_timeout',        'Processing Timeout'],
  ['disk_space_low',            'Disk Space Low'],
  ['memory_usage_high',         'Memory Usage High'],
  ['network_connectivity',      'Network Connectivity'],
  ['temperature_high',          'Temperature High'],
  ['nvidia_gpu_inactive',       'NVIDIA GPU Inactive'],
]

const ALERT_TYPE_OPTIONS: SelectOption[] = ALERT_TYPES.map(([value, label]) => ({ value, label }))
const ALERT_TYPE_LABEL = Object.fromEntries(ALERT_TYPES)

// ─── Default form state ───────────────────────────────────────────────────────

interface FormState {
  name: string
  description: string
  is_active: boolean
  condition_type: 'threshold' | 'string_match' | 'status_check' | 'combined'
  field_path: string
  operator: string
  threshold_value: number
  string_value: string
  alert_type: string
  alert_title: string
  alert_description_template: string
  severity: 'critical' | 'high' | 'medium' | 'low'
  device_type: 'onpremise' | 'gateway' | 'all'
  activation_status_filter: 'all' | 'activated' | 'trial'
  site_id: string
  client_id: string
  cooldown_minutes: number
}

const EMPTY_FORM: FormState = {
  name: '',
  description: '',
  is_active: true,
  condition_type: 'threshold',
  field_path: '',
  operator: 'gt',
  threshold_value: 0,
  string_value: '',
  alert_type: 'service_unreachable',
  alert_title: '',
  alert_description_template: '',
  severity: 'medium',
  device_type: 'onpremise',
  activation_status_filter: 'all',
  site_id: '',
  client_id: '',
  cooldown_minutes: 60,
}

// ─── Component ────────────────────────────────────────────────────────────────

export function CustomAlertRules({ onRuleChange }: CustomAlertRulesProps) {
  const [rules, setRules]             = useState<CustomAlertRule[]>([])
  const [loading, setLoading]         = useState(true)
  const [showForm, setShowForm]       = useState(false)
  const [editingRule, setEditingRule] = useState<CustomAlertRule | null>(null)
  const [formData, setFormData]       = useState<FormState>({ ...EMPTY_FORM })
  const [submitting, setSubmitting]   = useState(false)
  const [toast, setToast]             = useState<{ type: 'success' | 'error'; message: string } | null>(null)
  const [confirm, setConfirm]         = useState<{ message: string; onConfirm: () => void } | null>(null)
  const [formErrors, setFormErrors]   = useState<Record<string, string>>({})
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize]       = useState(10)
  const [search, setSearch]           = useState('')

  const filteredRules = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return rules
    return rules.filter(r =>
      r.name.toLowerCase().includes(q) ||
      r.description.toLowerCase().includes(q) ||
      (ALERT_TYPE_LABEL[r.alert_type] || r.alert_type).toLowerCase().includes(q) ||
      r.field_path.toLowerCase().includes(q) ||
      r.severity.toLowerCase().includes(q) ||
      (r.device_type || '').toLowerCase().includes(q) ||
      (r.site_id || '').toLowerCase().includes(q) ||
      (r.client_id || '').toLowerCase().includes(q)
    )
  }, [rules, search])

  const totalPages = Math.max(1, Math.ceil(filteredRules.length / pageSize))
  const pagedRules = useMemo(() => {
    const start = (currentPage - 1) * pageSize
    return filteredRules.slice(start, start + pageSize)
  }, [filteredRules, currentPage, pageSize])

  const dismissToast = useCallback(() => setToast(null), [])
  const showToast = (type: 'success' | 'error', message: string) => setToast({ type, message })

  const loadRules = async () => {
    setLoading(true)
    try {
      const response = await apiService.getCustomAlertRules()
      if (response.success && Array.isArray(response.data)) {
        setRules(response.data)
        setCurrentPage(1)
      } else {
        setRules([])
      }
    } catch {
      setRules([])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { loadRules() }, [])
  useEffect(() => { setCurrentPage(1) }, [search])

  const resetForm = () => { setFormData({ ...EMPTY_FORM }); setFormErrors({}) }

  const closeForm = () => {
    setShowForm(false)
    setEditingRule(null)
    resetForm()
  }

  const validate = (): boolean => {
    const e: Record<string, string> = {}
    if (!formData.name.trim())                        e.name = 'Name is required'
    if (!formData.description.trim())                 e.description = 'Description is required'
    if (!formData.field_path.trim())                  e.field_path = 'Field path is required'
    if (formData.condition_type === 'threshold' && (isNaN(formData.threshold_value) || formData.threshold_value === null))
                                                      e.threshold_value = 'Threshold value is required'
    if ((formData.condition_type === 'string_match' || formData.condition_type === 'status_check') && !formData.string_value.trim())
                                                      e.string_value = 'This field is required'
    if (!formData.alert_title.trim())                 e.alert_title = 'Alert title is required'
    if (!formData.alert_description_template.trim())  e.alert_description_template = 'Description template is required'
    if (!formData.cooldown_minutes || formData.cooldown_minutes < 1)
                                                      e.cooldown_minutes = 'Must be at least 1 minute'
    setFormErrors(e)
    return Object.keys(e).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!validate()) return

    const duplicate = rules.find(
      r => r.name.trim().toLowerCase() === formData.name.trim().toLowerCase()
        && r.device_type === formData.device_type
        && r.id !== editingRule?.id
    )
    if (duplicate) {
      showToast('error', `A rule named "${duplicate.name}" already exists for this device type.`)
      return
    }

    setSubmitting(true)
    try {
      const response = editingRule
        ? await apiService.updateCustomAlertRule(editingRule.id, formData)
        : await apiService.createCustomAlertRule(formData)

      if (!response.success) {
        showToast('error', typeof response.error === 'string' ? response.error : JSON.stringify(response.error))
        return
      }

      showToast('success', editingRule ? 'Rule updated successfully' : 'Rule created successfully')
      closeForm()
      loadRules()
      onRuleChange?.()
    } catch {
      showToast('error', 'Unexpected error saving rule')
    } finally {
      setSubmitting(false)
    }
  }

  const handleDelete = (rule: CustomAlertRule) => {
    if (rule.is_active) {
      showToast('error', 'Deactivate the rule before deleting it')
      return
    }
    setConfirm({
      message: 'Are you sure you want to delete this rule? This action cannot be undone.',
      onConfirm: async () => {
        setConfirm(null)
        try {
          await apiService.deleteCustomAlertRule(rule.id)
          showToast('success', 'Rule deleted')
          loadRules()
          onRuleChange?.()
        } catch {
          showToast('error', 'Error deleting rule')
        }
      },
    })
  }

  const handleToggleActive = async (rule: CustomAlertRule) => {
    try {
      await apiService.updateCustomAlertRule(rule.id, { ...rule, is_active: !rule.is_active })
      showToast('success', rule.is_active ? `"${rule.name}" deactivated` : `"${rule.name}" activated`)
      setRules(prev => prev.map(r => r.id === rule.id ? { ...r, is_active: !rule.is_active } : r))
      onRuleChange?.()
    } catch {
      showToast('error', 'Error toggling rule status')
    }
  }

  const handleEdit = (rule: CustomAlertRule) => {
    setEditingRule(rule)
    setFormData({
      name: rule.name,
      description: rule.description,
      is_active: rule.is_active,
      condition_type: rule.condition_type,
      field_path: rule.field_path,
      operator: rule.operator,
      threshold_value: rule.threshold_value || 0,
      string_value: rule.string_value || '',
      alert_type: rule.alert_type,
      alert_title: rule.alert_title,
      alert_description_template: rule.alert_description_template,
      severity: rule.severity,
      device_type: rule.device_type || 'onpremise',
      activation_status_filter: rule.activation_status_filter || 'all',
      site_id: rule.site_id || '',
      client_id: rule.client_id || '',
      cooldown_minutes: rule.cooldown_minutes,
    })
    setShowForm(true)
  }

  const set = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }))
    if (formErrors[field]) setFormErrors(prev => { const n = { ...prev }; delete n[field]; return n })
  }

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <div style={{ maxWidth: '900px', margin: '0 auto' }}>
      {toast && <Toast type={toast.type} message={toast.message} onClose={dismissToast} />}
      {confirm && (
        <ConfirmDialog
          message={confirm.message}
          confirmLabel="Delete"
          danger
          onConfirm={confirm.onConfirm}
          onCancel={() => setConfirm(null)}
        />
      )}

      {/* Header row */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', gap: '16px', flexWrap: 'wrap' }}>
        <div style={{ flexShrink: 0 }}>
          <h2 style={{ fontSize: '20px', fontWeight: 700, color: '#e2e8f0', margin: 0 }}>
            Custom Alert Rules
          </h2>
          <p style={{ fontSize: '13px', color: '#64748b', margin: '4px 0 0' }}>
            {rules.length} {rules.length === 1 ? 'rule' : 'rules'} configured
          </p>
        </div>

        {/* Search */}
        {!showForm && rules.length > 0 && (
          <div style={{ position: 'relative', flex: '1 1 220px', maxWidth: '360px' }}>
            <svg
              xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24"
              fill="none" stroke="#64748b" strokeWidth="2"
              style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}
            >
              <circle cx="11" cy="11" r="8" /><path d="M21 21l-4.35-4.35" />
            </svg>
            <input
              type="text"
              placeholder="Search rules…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              style={{
                ...S.input,
                paddingLeft: '32px',
                paddingRight: search ? '32px' : '12px',
              }}
            />
            {search && (
              <button
                onClick={() => setSearch('')}
                style={{
                  position: 'absolute', right: '8px', top: '50%', transform: 'translateY(-50%)',
                  background: 'none', border: 'none', cursor: 'pointer', color: '#64748b', padding: '2px',
                  display: 'flex', alignItems: 'center',
                }}
              >
                <X size={13} />
              </button>
            )}
          </div>
        )}

        {!showForm && (
          <button style={{ ...S.btnPrimary, flexShrink: 0 }} onClick={() => setShowForm(true)}>
            <Plus size={15} /> New Rule
          </button>
        )}
      </div>

      {/* Form */}
      {showForm && (
        <div style={S.formCard}>
          <h3 style={{ fontSize: '16px', fontWeight: 600, color: '#e2e8f0', margin: '0 0 20px' }}>
            {editingRule ? 'Edit Rule' : 'Create Rule'}
          </h3>
          <form onSubmit={handleSubmit}>

            {/* Row 1: name + condition type */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
              <div>
                <label style={S.label}>Name *</label>
                <input
                  style={{ ...S.input, borderColor: formErrors.name ? 'rgba(239,68,68,0.6)' : undefined }}
                  value={formData.name}
                  onChange={e => set('name', e.target.value)}
                />
                {formErrors.name && <span style={S.fieldError}>{formErrors.name}</span>}
              </div>
              <div>
                <label style={S.label}>Condition Type *</label>
                <CustomSelect
                  value={formData.condition_type}
                  onChange={v => set('condition_type', v)}
                  options={CONDITION_TYPE_OPTIONS}
                />
              </div>
            </div>

            {/* Description */}
            <div style={{ marginBottom: '16px' }}>
              <label style={S.label}>Description *</label>
              <textarea
                style={{ ...S.textarea, borderColor: formErrors.description ? 'rgba(239,68,68,0.6)' : undefined }}
                value={formData.description}
                onChange={e => set('description', e.target.value)}
              />
              {formErrors.description && <span style={S.fieldError}>{formErrors.description}</span>}
            </div>

            {/* Row 2: field path + operator */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
              <div>
                <label style={S.label}>Field Path *</label>
                <input
                  style={{ ...S.input, borderColor: formErrors.field_path ? 'rgba(239,68,68,0.6)' : undefined }}
                  value={formData.field_path}
                  placeholder="e.g. images_failed_current"
                  onChange={e => set('field_path', e.target.value)}
                />
                {formErrors.field_path && <span style={S.fieldError}>{formErrors.field_path}</span>}
              </div>
              <div>
                <label style={S.label}>Operator *</label>
                <CustomSelect
                  value={formData.operator}
                  onChange={v => set('operator', v)}
                  options={OPERATOR_OPTIONS}
                />
              </div>
            </div>

            {/* Threshold or string value */}
            {formData.condition_type === 'threshold' && (
              <div style={{ marginBottom: '16px' }}>
                <label style={S.label}>Threshold value *</label>
                <input
                  style={{ ...S.input, borderColor: formErrors.threshold_value ? 'rgba(239,68,68,0.6)' : undefined }}
                  type="number"
                  value={formData.threshold_value}
                  onChange={e => set('threshold_value', parseFloat(e.target.value))}
                />
                {formErrors.threshold_value && <span style={S.fieldError}>{formErrors.threshold_value}</span>}
              </div>
            )}
            {(formData.condition_type === 'string_match' || formData.condition_type === 'status_check') && (
              <div style={{ marginBottom: '16px' }}>
                <label style={S.label}>
                  {formData.condition_type === 'status_check' ? 'Status (alert when equal) *' : 'String value *'}
                </label>
                <input
                  style={{ ...S.input, borderColor: formErrors.string_value ? 'rgba(239,68,68,0.6)' : undefined }}
                  value={formData.string_value}
                  placeholder={formData.condition_type === 'status_check' ? 'e.g. inactive' : ''}
                  onChange={e => set('string_value', e.target.value)}
                />
                {formErrors.string_value && <span style={S.fieldError}>{formErrors.string_value}</span>}
              </div>
            )}

            <p style={S.sectionTitle}>Alert configuration</p>

            {/* Alert type */}
            <div style={{ marginBottom: '16px' }}>
              <label style={S.label}>Alert type *</label>
              <CustomSelect
                value={formData.alert_type}
                onChange={v => set('alert_type', v)}
                options={ALERT_TYPE_OPTIONS}
              />
            </div>

            {/* Alert title + severity */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
              <div>
                <label style={S.label}>Alert title *</label>
                <input
                  style={{ ...S.input, borderColor: formErrors.alert_title ? 'rgba(239,68,68,0.6)' : undefined }}
                  value={formData.alert_title}
                  onChange={e => set('alert_title', e.target.value)}
                />
                {formErrors.alert_title && <span style={S.fieldError}>{formErrors.alert_title}</span>}
              </div>
              <div>
                <label style={S.label}>Severity *</label>
                <CustomSelect
                  value={formData.severity}
                  onChange={v => set('severity', v)}
                  options={SEVERITY_OPTIONS}
                />
              </div>
            </div>

            {/* Device type / activation status */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
              <div>
                <label style={S.label}>Device type *</label>
                <CustomSelect
                  value={formData.device_type}
                  onChange={v => set('device_type', v)}
                  options={DEVICE_TYPE_OPTIONS}
                />
              </div>
              <div>
                <label style={S.label}>Machine type *</label>
                <CustomSelect
                  value={formData.activation_status_filter}
                  onChange={v => set('activation_status_filter', v)}
                  options={ACTIVATION_STATUS_OPTIONS}
                />
              </div>
            </div>

            {/* Description template */}
            <div style={{ marginBottom: '16px' }}>
              <label style={S.label}>
                Description template *
                <span style={{ marginLeft: '6px', fontSize: '11px', color: '#64748b', textTransform: 'none', letterSpacing: 0 }}>
                  — use {'{value}'}, {'{threshold}'} or {'{field_name}'}
                </span>
              </label>
              <textarea
                style={{ ...S.textarea, borderColor: formErrors.alert_description_template ? 'rgba(239,68,68,0.6)' : undefined }}
                value={formData.alert_description_template}
                placeholder="e.g. CPU usage is at {value}% (threshold: {threshold}%)"
                onChange={e => set('alert_description_template', e.target.value)}
              />
              {formErrors.alert_description_template && <span style={S.fieldError}>{formErrors.alert_description_template}</span>}
            </div>

            <p style={S.sectionTitle}>Scope (optional)</p>

            {/* Site / client / cooldown */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 140px', gap: '16px', marginBottom: '16px' }}>
              <div>
                <label style={S.label}>Site ID</label>
                <input style={S.input} value={formData.site_id}
                  placeholder="Leave blank for global"
                  onChange={e => set('site_id', e.target.value)} />
              </div>
              <div>
                <label style={S.label}>Client ID</label>
                <input style={S.input} value={formData.client_id}
                  placeholder="Leave blank for global"
                  onChange={e => set('client_id', e.target.value)} />
              </div>
              <div>
                <label style={S.label}>Cooldown (min) *</label>
                <input
                  style={{ ...S.input, borderColor: formErrors.cooldown_minutes ? 'rgba(239,68,68,0.6)' : undefined }}
                  type="number"
                  value={formData.cooldown_minutes}
                  onChange={e => set('cooldown_minutes', parseInt(e.target.value))}
                />
                {formErrors.cooldown_minutes && <span style={S.fieldError}>{formErrors.cooldown_minutes}</span>}
              </div>
            </div>

            {/* Active */}
            <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', marginBottom: '20px', userSelect: 'none' }}>
              <input
                type="checkbox"
                checked={formData.is_active}
                onChange={e => set('is_active', e.target.checked)}
                style={{ width: '16px', height: '16px', accentColor: '#00A9A7', cursor: 'pointer' }}
              />
              <span style={{ fontSize: '14px', color: '#e2e8f0' }}>Active</span>
            </label>

            {/* Buttons */}
            <div style={{ display: 'flex', gap: '10px' }}>
              <button type="submit" style={{ ...S.btnPrimary, opacity: submitting ? 0.6 : 1 }} disabled={submitting}>
                {submitting ? 'Saving…' : editingRule ? 'Update Rule' : 'Create Rule'}
              </button>
              <button type="button" style={S.btnOutline} onClick={closeForm}>
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Rules list */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '48px', color: '#64748b' }}>
          <div style={{ width: '32px', height: '32px', border: '2px solid rgba(255,255,255,0.1)', borderTopColor: '#00A9A7', borderRadius: '50%', animation: 'spin 0.8s linear infinite', margin: '0 auto 12px' }} />
          Loading rules…
        </div>
      ) : rules.length === 0 ? (
        <div style={{ ...S.card, textAlign: 'center', padding: '48px' }}>
          <Settings size={40} style={{ color: '#64748b', margin: '0 auto 12px', display: 'block' }} />
          <p style={{ color: '#94a3b8', fontWeight: 600, marginBottom: '4px' }}>No custom alert rules</p>
          <p style={{ color: '#64748b', fontSize: '13px', marginBottom: '20px' }}>
            Create your first rule to monitor specific conditions.
          </p>
          <button style={S.btnPrimary} onClick={() => setShowForm(true)}>
            <Plus size={15} /> Create first rule
          </button>
        </div>
      ) : filteredRules.length === 0 ? (
        <div style={{ ...S.card, textAlign: 'center', padding: '40px' }}>
          <p style={{ color: '#94a3b8', fontWeight: 600, marginBottom: '4px' }}>No rules match "{search}"</p>
          <p style={{ color: '#64748b', fontSize: '13px', marginBottom: '16px' }}>Try a different search term.</p>
          <button style={S.btnOutline} onClick={() => setSearch('')}>Clear search</button>
        </div>
      ) : (
        <>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {pagedRules.map(rule => (
              <div key={rule.id} style={S.card}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px', flexWrap: 'wrap' }}>
                      <span style={{ fontWeight: 600, color: '#e2e8f0', fontSize: '15px' }}>{rule.name}</span>
                      <SeverityBadge severity={rule.severity} />
                      <span style={{
                        fontSize: '11px', fontWeight: 600,
                        color: rule.is_active ? '#00A9A7' : '#64748b',
                        background: rule.is_active ? 'rgba(0,169,167,0.12)' : 'rgba(255,255,255,0.05)',
                        border: `1px solid ${rule.is_active ? 'rgba(0,169,167,0.3)' : 'rgba(255,255,255,0.08)'}`,
                        borderRadius: '6px', padding: '2px 8px',
                      }}>
                        {rule.is_active ? 'ACTIVE' : 'INACTIVE'}
                      </span>
                    </div>
                    <p style={{ fontSize: '13px', color: '#64748b', margin: '0 0 8px' }}>{rule.description}</p>
                    <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', fontSize: '12px', color: '#94a3b8' }}>
                      <span>📍 {rule.field_path} {rule.operator} {rule.threshold_value ?? rule.string_value}</span>
                      <span>🔔 {ALERT_TYPE_LABEL[rule.alert_type] || rule.alert_type}</span>
                      <span>🖥 {rule.device_type || 'onpremise'}</span>
                      {rule.activation_status_filter && rule.activation_status_filter !== 'all' && (
                        <span>🏷 {rule.activation_status_filter}</span>
                      )}
                      <span>⏱ {rule.cooldown_minutes} min</span>
                      {rule.site_id && <span>📍 {rule.site_id}</span>}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '6px', marginLeft: '12px' }}>
                    <button style={S.btnIcon} title={rule.is_active ? 'Deactivate' : 'Activate'}
                      onClick={() => handleToggleActive(rule)}>
                      {rule.is_active ? <Pause size={14} /> : <Play size={14} />}
                    </button>
                    <button style={S.btnIcon} title="Edit" onClick={() => handleEdit(rule)}>
                      <Edit size={14} />
                    </button>
                    <button style={{ ...S.btnIcon, color: '#ef4444' }} title="Delete"
                      onClick={() => handleDelete(rule)}>
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Pagination footer */}
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            marginTop: '16px', flexWrap: 'wrap', gap: '12px',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <span style={{ fontSize: '13px', color: '#64748b' }}>
                Showing {Math.min((currentPage - 1) * pageSize + 1, filteredRules.length)}–{Math.min(currentPage * pageSize, filteredRules.length)} of {filteredRules.length}{search ? ` of ${rules.length}` : ''} {filteredRules.length === 1 ? 'rule' : 'rules'}
              </span>
              <CustomSelect
                value={String(pageSize)}
                onChange={v => { setPageSize(Number(v)); setCurrentPage(1) }}
                options={PAGE_SIZE_OPTIONS}
                compact
              />
            </div>
            {totalPages > 1 && (
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={setCurrentPage}
              />
            )}
          </div>
        </>
      )}

      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        input[type=number]::-webkit-inner-spin-button { opacity: 0.4; }
      `}</style>
    </div>
  )
}
